import { Button } from "@mantine/core";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ApiResponse } from "../../api";
import { ModalFooter } from "../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../mantine/notifications/useToast";
import type { WizardFormModeProps } from "../../types/wizard.types";
import type { GhostNode } from "../../utils/backend-types";
import { applyHighlightingToObjectFormFieldData } from "../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { FormFieldController } from "../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { GHOST_NODE_FORM_FIELD_DATA } from "./GhostNodeWizard.constants";

type GhostNodeFormProps = {
  onCancel: () => void;
  onSubmit: (data: GhostNode) => Promise<ApiResponse>;
} & WizardFormModeProps<GhostNode>;

function GhostNodeForm({
  onCancel,
  onSubmit,
  ...modeProps
}: GhostNodeFormProps) {
  const { showErrorToast } = useToast();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch
  } = useForm<GhostNode>({
    defaultValues:
      modeProps.mode === "edit" ? modeProps.wizardObject : undefined
  });

  const { formFieldData } = applyHighlightingToObjectFormFieldData(
    GHOST_NODE_FORM_FIELD_DATA,
    watch,
    modeProps.mode === "edit" ? modeProps : undefined
  );

  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onValidSubmit: SubmitHandler<GhostNode> = async (data) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await onSubmit(data);
      return response;
    } catch (error) {
      setIsSubmitting(false);
      setErrorsFromResponseData<GhostNode>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  function handleClickCancel() {
    reset();
    onCancel();
  }

  return (
    <form className="GhostNodeForm" onSubmit={submitCallback}>
      <FormFieldController
        control={control}
        data={formFieldData.name}
        error={errors.name}
      />

      <ModalFooter>
        <Button
          disabled={isSubmitting}
          variant="default"
          onClick={handleClickCancel}
        >
          Abbrechen
        </Button>
        <Button
          color="secondary"
          loading={isSubmitting}
          onClick={submitCallback}
        >
          {modeProps.mode === "edit" ? "Speichern" : "Erstellen"}
        </Button>
      </ModalFooter>
    </form>
  );
}

export { GhostNodeForm, GhostNodeFormProps };
